import { useEffect, useRef } from "react";
import { Outlet, useLocation, type RouteObject } from "react-router";
import { useTalentWorkspace } from "../../app/session";
import { ArchivedNotice } from "./ui";
import { useLang } from "./sprache";
import { setCreatorFrameLang } from "./fehler";
import { useRahmenSprache } from "./rahmenSprache";
import { CocoRundgangCreator, SeitenEinfuehrung } from "./Einfuehrung";
import { setManifest } from "../../pwa";

/**
 * Gemeinsamer Rahmen aller Creator-Seiten (auch Kalender, Nachrichten und Benachrichtigungen aus der Kommunikation):
 * Sprache der geteilten Bausteine, Sperre im Archiv, Zeilenumbruch im Chat am Handy, Sprung zur gewählten Rechnung am
 * Handy und die kurze Einführung je Seite. Die Seiten selbst bleiben unverändert.
 */

/** Wege der Kommunikation, die es mit archiviertem Profil nicht gibt (Befund 19). */
const CLOSED_WHEN_ARCHIVED = ["/creator/kalender", "/creator/nachrichten", "/creator/benachrichtigungen"];

const under = (pathname: string, base: string) => pathname === base || pathname.startsWith(base + "/");

export function CreatorRahmen() {
  const ws = useTalentWorkspace();
  const lang = useLang();
  const { pathname, search } = useLocation();
  setCreatorFrameLang(lang);
  useRahmenSprache(lang);
  // Installierbare App: im Creator-Portal startet sie im Creator-Portal (Befund 7)
  useEffect(() => {
    setManifest("/creator");
    return () => setManifest(window.location.pathname);
  }, []);
  useTouchEnterNewline();
  useScrollToSelectedInvoice(pathname, search);
  if (ws.archived && CLOSED_WHEN_ARCHIVED.some((p) => under(pathname, p))) {
    return <ArchivedNotice key={ws.link.talent_id} chat={under(pathname, "/creator/nachrichten")} />;
  }
  return (
    <>
      <Outlet />
      {!ws.archived && <SeitenEinfuehrung key={ws.link.talent_id} />}
      {!ws.archived && <CocoRundgangCreator key={`rundgang-${ws.link.talent_id}`} />}
    </>
  );
}

/** Wege des Creator-Portals und der Kommunikation unter einem Rahmen (app/routes.tsx). */
export function unterCreatorRahmen(routes: RouteObject[]): RouteObject[] {
  return [{ element: <CreatorRahmen />, children: routes }];
}

/** Touch-Gerät (Handy, Tablet): keine Umschalttaste auf der Bildschirmtastatur. */
export function isTouchDevice(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
}

/** Ist das Feld das Eingabefeld des Chats? (Hinweis #chat-hinweis gehört zum selben Formular) */
export function isChatComposer(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLTextAreaElement)) return false;
  return (el.getAttribute("aria-describedby") ?? "").split(/\s+/).includes("chat-hinweis") || !!el.closest("form")?.querySelector("#chat-hinweis");
}

/**
 * Befund 18: Am Handy beginnt Enter im Chat eine neue Zeile; gesendet wird mit dem Knopf „Senden“. Das Ereignis erreicht
 * den Senden-Handler der Seite nicht (Abfangen vor React), der Zeilenumbruch selbst bleibt. Am Rechner gilt weiter:
 * Enter sendet, Umschalt + Enter beginnt eine neue Zeile. Den Hinweistext passt die Rahmen-Sprache an.
 */
function useTouchEnterNewline() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.shiftKey || e.isComposing || !isTouchDevice() || !isChatComposer(e.target)) return;
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
}

/**
 * Befund 17: Tippt man am Handy in der Liste auf eine Rechnung, wechselt die Detailkarte oben. Liegt sie außerhalb des
 * sichtbaren Bereichs, springt die Seite dorthin und setzt den Fokus auf ihre Überschrift.
 */
function useScrollToSelectedInvoice(pathname: string, search: string) {
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (pathname !== "/creator/rechnungen") {
      last.current = null;
      return;
    }
    const id = new URLSearchParams(search).get("rechnung");
    const before = last.current;
    last.current = id;
    if (!id || id === before) return;
    let tries = 0;
    let frame = 0;
    const tick = () => {
      const heading = document.getElementById(`rechnung-${id}`);
      if (!heading) {
        if (++tries < 90) frame = requestAnimationFrame(tick);
        return;
      }
      const box = heading.closest("section") ?? heading;
      const top = box.getBoundingClientRect().top;
      const header = document.querySelector(".portal-header")?.getBoundingClientRect().height ?? 0;
      if (top < header || top > window.innerHeight * 0.6) {
        const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({ top: Math.max(0, window.scrollY + top - header - 12), behavior: reduced ? "auto" : "smooth" });
      }
      if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [pathname, search]);
}
